import { useState, useEffect } from "react";
import { registerPushIfNative } from "@/lib/pushNotifications";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { TagsProvider } from "@/hooks/useTags";
import { PomodoroProvider } from "@/hooks/usePomodoroTimer";
import { SubscriptionProvider } from "@/hooks/useSubscription";
import { SpacesProvider } from "@/hooks/useSpaces";
import { TourProvider } from "@/hooks/useProductTour";
import { PomodoroTimer } from "@/components/PomodoroTimer";
import { SplashScreen } from "@/components/SplashScreen";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { getWindowType } from "@/lib/desktopBridge";
import { DesktopSyncInner } from "@/hooks/useDesktopSync";
import { MenuBarPopover } from "@/components/desktop/MenuBarPopover";
import { FloatingTimerPill } from "@/components/desktop/FloatingTimerPill";
import Landing from "./pages/Landing";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Onboarding from "./pages/Onboarding";
import Privacy from "./pages/Privacy";
import Security from "./pages/Security";
import Terms from "./pages/Terms";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import CheckoutCanceled from "./pages/CheckoutCanceled";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

/**
 * Desktop widget windows (menu bar popover, floating pill) load the same
 * index.html with a `?__win=` flag. They receive timer state purely over IPC,
 * so they mount a lightweight, transparent shell — no auth, router, or
 * Pomodoro context. The main window renders the full app below.
 */
const DesktopWidget = ({ kind }: { kind: "menubar" | "floating" }) => {
  useEffect(() => {
    document.documentElement.style.background = "transparent";
    document.body.style.background = "transparent";
  }, []);

  return (
    <div className="dark w-screen h-screen bg-transparent overflow-hidden">
      {kind === "menubar" ? (
        <div className="p-2">
          <MenuBarPopover />
        </div>
      ) : (
        <FloatingTimerPill />
      )}
    </div>
  );
};

const App = () => {
  const windowType = getWindowType();
  if (windowType) {
    return <DesktopWidget kind={windowType} />;
  }

  return <MainApp />;
};

const MainApp = () => {
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    // Minimum splash display time
    const timer = setTimeout(() => {
      setShowSplash(false);
    }, 1800);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    registerPushIfNative();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SubscriptionProvider>
          <SpacesProvider>
            <TagsProvider>
              <PomodoroProvider>
                <DesktopSyncInner />
                <TourProvider>
                <TooltipProvider>
                  <SplashScreen isVisible={showSplash} />
                  <Toaster />
                  <Sonner />
                  <ErrorBoundary>
                    <BrowserRouter>
                      <a
                        href="#main-content"
                        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:bg-background focus:px-4 focus:py-2 focus:rounded-md focus:ring-2 focus:ring-primary focus:outline-none"
                      >
                        Skip to main content
                      </a>
                      <Routes>
                        <Route path="/" element={<Landing />} />
                        <Route path="/app" element={<Index />} />
                        <Route path="/auth" element={<Auth />} />
                        <Route path="/onboarding" element={<Onboarding />} />
                        <Route path="/privacy" element={<Privacy />} />
                        <Route path="/security" element={<Security />} />
                        <Route path="/terms" element={<Terms />} />
                        <Route path="/checkout/success" element={<CheckoutSuccess />} />
                        <Route path="/checkout/canceled" element={<CheckoutCanceled />} />
                        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </BrowserRouter>
                    <PomodoroTimer />
                  </ErrorBoundary>
                </TooltipProvider>
              </TourProvider>
            </PomodoroProvider>
            </TagsProvider>
          </SpacesProvider>
        </SubscriptionProvider>
        </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
